import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaSearch,
  FaSortAmountDown,
  FaFolderOpen,
  FaHome,
  FaFolder,
  FaFileAlt,
  FaSignOutAlt,
  FaUser,
} from "react-icons/fa";
import logo from "../../../assets/irc-logo-rb.png";
import Sidebar from "./Sidebar";

const ProjectsForCompetition = () => {
  const { idcompetition } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [projets, setProjets] = useState([]);
  // NEW: map of projectId -> true if already evaluated
  const [evaluatedMap, setEvaluatedMap] = useState({});
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [competitionTitle, setCompetitionTitle] = useState("");
  const perPage = 6;
  const handleToggleSidebar = () => setSidebarOpen((s) => !s);

  useEffect(() => {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };
    const idevaluateur = getCookie("idevaluateur");
    if (!idevaluateur) return;

    const apiBase =
      process.env.REACT_APP_API_BASE ||
      process.env.REACT_APP_BACKEND_URL ||
      `${window.location.protocol}//${window.location.hostname}:3001`;
    setLoading(true);
    fetch(
      `${apiBase}/api/evaluateur/competitions/${encodeURIComponent(
        idcompetition
      )}/projets?idevaluateur=${encodeURIComponent(idevaluateur)}`
    )
      .then((r) => r.json())
      .then((json) => {
        if (json?.status === "success")
          setProjets(Array.isArray(json.projets) ? json.projets : []);
        else setError(json?.message || "Erreur de chargement");
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [idcompetition]);

  // Fetch competition title for header (Node first, PHP fallback)
  useEffect(() => {
    const loadCompetition = async () => {
      try {
        const apiBase =
          process.env.REACT_APP_API_BASE ||
          process.env.REACT_APP_BACKEND_URL ||
          `${window.location.protocol}//${window.location.hostname}:3001`;
        const res = await fetch(
          `${apiBase}/api/evaluateur/competitions/${encodeURIComponent(
            idcompetition
          )}`
        );
        const json = await res.json();
        const comp = json?.competition || json?.data || json?.item;
        if (json?.status === "success" && comp) {
          setCompetitionTitle(comp.titre || comp.libelle || "");
          return;
        }
      } catch {}
      try {
        if (!process.env.REACT_APP_BASE_URL) return;
        const r = await fetch(
          `${process.env.REACT_APP_BASE_URL}/getCompitition.php`
        );
        const arr = await r.json();
        const found =
          Array.isArray(arr) &&
          arr.find((c) => String(c.idcompetition) === String(idcompetition));
        if (found) setCompetitionTitle(found.titre || found.libelle || "");
      } catch {}
    };
    loadCompetition();
  }, [idcompetition]);

  const filtered = useMemo(() => {
    const norm = (v) => {
      if (v === null || v === undefined) return "";
      try {
        return String(v)
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
      } catch {
        return String(v).toLowerCase();
      }
    };
    const term = norm(search.trim());
    let list = Array.isArray(projets) ? projets : [];

    if (term) {
      list = list.filter((p) => {
        const haystack = [
          p.titre,
          p.titre_projet,
          p.responsable,
          p.description,
          p.libelle,
        ]
          .map(norm)
          .join(" ");
        const idStr = norm(p.idprojet);
        return haystack.includes(term) || idStr.includes(term);
      });
    }

    if (sort === "title") {
      const getTitle = (p) => String(p.titre || p.titre_projet || "");
      list = [...list].sort((a, b) => getTitle(a).localeCompare(getTitle(b)));
    } else if (sort === "recent") {
      const getTs = (p) =>
        new Date(
          p.date_debut_projet || p.date_creation || p.date_debut || 0
        ).getTime();
      list = [...list].sort((a, b) => getTs(b) - getTs(a));
    }

    return list;
  }, [projets, search, sort]);

  // Reset to first page on search/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, sort]);

  // Compute pagination slice
  const totalPages = Math.ceil(filtered.length / perPage) || 1;
  const start = (currentPage - 1) * perPage;
  const currentItems = filtered.slice(start, start + perPage);

  const skeletons = Array.from({ length: 6 });

  const container = {
    display: "flex",
    minHeight: "100vh",
    background: "#e4d2d2",
  };
  const content = {
    flex: 1,
    padding: 24,
    marginLeft: "17rem",
    width: "calc(100vw - 20rem)",
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    paddingBottom: 80, // space for fixed pagination
  };
  const headerBar = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fff",
    border: "1px solid #e6e8eb",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 8px 16px rgba(16,24,40,0.08)",
    marginBottom: 16,
  };
  const leftGroup = { display: "flex", alignItems: "center", gap: 12 };
  const backBtn = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #d0d5dd",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  };
  const title = {
    margin: 0,
    fontSize: "1.25rem",
    fontWeight: 800,
    color: "#0b7a3e",
  };
  const chip = {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 700,
  };
  const rightGroup = { display: "flex", alignItems: "center", gap: 8 };
  const inputWrap = { position: "relative" };
  const searchInput = {
    padding: "10px 12px 10px 36px",
    borderRadius: 8,
    border: "1px solid #d0d5dd",
    background: "#fff",
    width: 260,
  };
  const searchIcon = {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
  };
  const sortBtn = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid #d0d5dd",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 16,
  };
  const card = {
    background: "#fff",
    border: "1px solid #e6e8eb",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 6px 16px rgba(16,24,40,0.06)",
    transition: "transform .1s ease, box-shadow .1s ease",
  };
  const cardHover = {
    transform: "translateY(-2px)",
    boxShadow: "0 10px 20px rgba(16,24,40,0.12)",
  };
  const cardHeader = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  };
  const cardTitle = {
    margin: 0,
    fontSize: "1.05rem",
    fontWeight: 800,
    color: "#111827",
  };
  const idBadge = {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 700,
  };
  // NEW: style for evaluated badge
  const evaluatedBadge = {
    marginLeft: 6,
    padding: "2px 8px",
    borderRadius: 999,
    background: "#dcfce7",
    border: "1px solid #86efac",
    color: "#166534",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
  };
  const meta = { margin: "6px 0", color: "#374151" };
  const openBtn = {
    padding: "8px 12px",
    borderRadius: 8,
    background: "#0b7a3e",
    color: "#fff",
    border: "none",
    fontWeight: 700,
    cursor: "pointer",
  };

  const errorBox = {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  };
  const emptyWrap = {
    background: "#fff",
    border: "1px solid #e6e8eb",
    borderRadius: 12,
    padding: 24,
    textAlign: "center",
    color: "#475569",
  };
  const skel = {
    background: "linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9)",
    backgroundSize: "200% 100%",
    animation: "shine 1.2s infinite",
    height: 18,
    borderRadius: 6,
  };

  const [hoverIndex, setHoverIndex] = useState(-1);

  const onSortClick = () =>
    setSort((s) => (s === "recent" ? "title" : "recent"));

  const paginationContainer = {
    position: "fixed",
    bottom: 24,
    left: "55%",
    transform: "translateX(-50%)",
    display: "flex",
    justifyContent: "center",
    padding: "8px 12px",
    background: "transparent",
    zIndex: 10,
  };
  const paginationList = {
    listStyle: "none",
    display: "flex",
    gap: 8,
    padding: 0,
    margin: 0,
  };
  const pageBtn = (active) => ({
    padding: "6px 10px",
    borderRadius: 6,
    border: active ? "1px solid #0b7a3e" : "1px solid #d0d5dd",
    background: active ? "#0b7a3e" : "#fff",
    color: active ? "#fff" : "#0b7a3e",
    fontWeight: 700,
    cursor: "pointer",
  });

  // Helper to display only the date part
  const formatDate = (val) => {
    if (!val) return "-";
    const s = String(val);
    if (s.includes("T")) return s.split("T")[0];
    if (s.includes(" ")) return s.split(" ")[0];
    return s;
  };

  useEffect(() => {
    if (!projets.length) return;
    let cancelled = false;
    const apiBase =
      process.env.REACT_APP_API_BASE ||
      process.env.REACT_APP_BACKEND_URL ||
      `${window.location.protocol}//${window.location.hostname}:3001`;
    const getCookie = (name) => {
      const m = document.cookie.split(';').find(c=>c.trim().startsWith(name+'='));
      return m ? decodeURIComponent(m.split('=')[1]) : null;
    };
    const idevaluateur = getCookie('idevaluateur');
    setEvaluatedMap({});
    (async () => {
      for (const p of projets) {
        try {
          const r = await fetch(
            `${apiBase}/api/project-evaluation?id_projet=${encodeURIComponent(
              p.idprojet
            )}&id_competition=${encodeURIComponent(idcompetition)}${idevaluateur ? `&idevaluateur=${encodeURIComponent(idevaluateur)}`:''}`
          );
          const j = await r.json();
          if (!cancelled && j.status === "success" && Array.isArray(j.data) && j.data.length) {
            setEvaluatedMap(prev => ({ ...prev, [p.idprojet]: true }));
          }
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [projets, idcompetition]);

  return (
    <div style={container}>
      <Sidebar onToggleSidebar={handleToggleSidebar} />
      <div style={content}>
        <style>{`@keyframes shine{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        <div style={headerBar}>
          <div style={leftGroup}>
            <button style={backBtn} onClick={() => navigate(-1)}>
              <FaArrowLeft /> Retour
            </button>
            <h2 style={title}>
              {competitionTitle || "Projets de la compétition"}
            </h2>
            <span style={chip}>№: {idcompetition}</span>
          </div>
          <div style={rightGroup}>
            <div style={inputWrap}>
              <FaSearch style={searchIcon} />
              <input
                type="text"
                placeholder="Rechercher un projet..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={searchInput}
              />
            </div>
            <button style={sortBtn} onClick={onSortClick}>
              <FaSortAmountDown />{" "}
              {sort === "recent" ? "Plus récents" : "Titre A→Z"}
            </button>
          </div>
        </div>

        {error && <div style={errorBox}>{error}</div>}

        <div style={grid}>
          {loading &&
            skeletons.map((_, i) => (
              <div key={i} style={{ ...card }}>
                <div
                  style={{ ...skel, width: "65%", height: 22, marginBottom: 10 }}
                />
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <div style={{ ...skel, width: 80, height: 22 }} />
                  <div style={{ ...skel, width: 60, height: 22 }} />
                </div>
                <div
                  style={{ ...skel, width: "100%", height: 14, marginBottom: 8 }}
                />
                <div
                  style={{ ...skel, width: "80%", height: 14, marginBottom: 8 }}
                />
                <div style={{ ...skel, width: 120, height: 36, marginTop: 12 }} />
              </div>
            ))}

          {!loading &&
            currentItems.map((p, idx) => {
              const submitter =
                [p.prenom, p.nom].filter(Boolean).join(" ") ||
                p.responsable ||
                "-";
              const submittedAt = formatDate(
                p.date_debut_projet || p.date_creation
              );
              const projTitle =
                p.titre || p.titre_projet || `Projet #${p.idprojet}`;
              return (
                <div
                  key={p.idprojet}
                  style={{ ...card, ...(hoverIndex === idx ? cardHover : null) }}
                  onMouseEnter={() => setHoverIndex(idx)}
                  onMouseLeave={() => setHoverIndex(-1)}
                >
                  <div style={cardHeader}>
                    <h3 style={cardTitle}>{projTitle}</h3>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={idBadge}>{`#${p.idprojet}`}</span>
                      {evaluatedMap[p.idprojet] && (
                        <span
                          style={evaluatedBadge}
                          title="Vous avez déjà évalué ce projet"
                        >
                          Évalué
                        </span>
                      )}
                    </div>
                  </div>
                  <p style={meta}>
                    <strong>Soumis par:</strong> {submitter}
                  </p>
                  <p style={meta}>
                    <strong>Soumis le:</strong> {submittedAt}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      marginTop: 12,
                    }}
                  >
                    <button
                      style={openBtn}
                      onClick={() =>
                        navigate(
                          `/evaluateur/competitions/${idcompetition}/projets/${p.idprojet}/forum`
                        )
                      }
                    >
                      <FaFolderOpen style={{ marginRight: 6 }} /> Ouvrir
                    </button>
                  </div>
                </div>
              );
            })}
        </div>

        {!loading && !error && filtered.length === 0 && (
          <div style={emptyWrap}>
            Aucun projet trouvé pour cette compétition.
          </div>
        )}

        {totalPages > 1 && (
          <div style={paginationContainer}>
            <ul style={paginationList}>
              {Array.from({ length: totalPages }, (_, i) => (
                <li key={i}>
                  <button
                    type="button"
                    style={pageBtn(currentPage === i + 1)}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsForCompetition;
